import { useState, useEffect } from "react";
import axios from "axios";
import { Button, Divider, List, Box } from "@mui/material";
import { Add } from "@mui/icons-material";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import Combobox from "../../../components/common/Combobox";
import { PopperFullWidth } from "../../../common/appStyles";
import ErrorMessage from "../../../components/common/ErrorMessage";
import NotificationItem from "../../../components/common/NotificationItem";
import ResolutionItem from "../../resolution/components/ResolutionItem";

export default function EventResolutions() {
  const {
    eventModel,
    setEventModel,
    eventResolutions,
    getEventResolutions,
    EVENTS_API,
    RESOLUTION_ENDPOINT,
    config,
    createEntity,
    deleteEntity,
  } = useGlobalContext();

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedResolution, setSelectedResolution] = useState(null);
  const [resolutionsData, setResolutionsData] = useState(null);

  const getResolutions = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${RESOLUTION_ENDPOINT}/`, requestConfig);

    setResolutionsData(data?.data?.Data);
  };

  const validateOperationResult = (result) => {
    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }
    requestEventResolutions(eventModel.Id);
  };

  const convertResolutionsToRequest = (resolutions) => {
    if (!resolutions) {
      return null;
    }

    const resolutionResult = resolutions.map((res) => ({
      EventId: 0,
      ResolutionId: res?.Resolution?.Id,
      CreatedBy: eventModel?.CreatedBy,
    }));
    return resolutionResult;
  };

  useEffect(() => {
    if (eventResolutions?.length > 0) {
      setEventModel({
        ...eventModel,
        ResolutionsInfo: eventResolutions,
        Resolutions: convertResolutionsToRequest(eventResolutions),
      });
    }
  }, [eventResolutions]);

  const requestEventResolutions = async (eventId) => {
    const result = await getEventResolutions(eventId);
    setEventModel({
      ...eventModel,
      ResolutionsInfo: result,
      Resolutions: convertResolutionsToRequest(result),
    });
  };

  useEffect(() => {
    getResolutions();
    if (eventModel?.Id > 0) {
      requestEventResolutions(eventModel?.Id);
    }
  }, []);

  const handleResolutionSelection = (selected) => {
    setSelectedResolution(selected);
  };

  const createResolutionForEvent = async (resolution) => {
    const result = await createEntity(
      `${EVENTS_API}/${eventModel.Id}/resolution`,
      {
        ...resolution,
        ResolutionId: resolution.Id,
        EventId: eventModel?.Id,
      },
      config
    );
    validateOperationResult(result);
    return result;
  };

  const deleteResolutionFromEvent = async (resolutionId) => {
    const result = await deleteEntity(
      `${EVENTS_API}/${eventModel.Id}/resolution/${resolutionId}`,
      config
    );
    validateOperationResult(result);
    return result;
  };

  const handleAddBtn = async () => {
    if (selectedResolution) {
      if (eventModel?.ResolutionsInfo?.length > 0) {
        /* eslint-disable no-plusplus */
        for (
          let index = 0;
          index < eventModel.ResolutionsInfo.length;
          index++
        ) {
          if (
            selectedResolution.Id ===
            eventModel.ResolutionsInfo[index].Resolution.Id
          ) {
            return;
          }
        }
      }

      if (eventModel?.Id > 0) {
        const resolutionResult = await createResolutionForEvent(
          selectedResolution
        );
        if (!resolutionResult.ok) {
          return;
        }
      }

      const resolutions = eventModel?.ResolutionsInfo ?? [];
      resolutions.push({
        Resolution: {
          ...selectedResolution,
          ResolutionId: selectedResolution.Id,
          EventId: eventModel?.Id,
        },
      });

      setEventModel({
        ...eventModel,
        ResolutionsInfo: resolutions,
        Resolutions: convertResolutionsToRequest(resolutions),
      });
    }

    setSelectedResolution(null);
  };

  const handleDeleteRequest = async (itemId) => {
    if (itemId <= 0) return;

    if (eventModel?.ResolutionsInfo?.length > 0) {
      /* eslint-disable no-plusplus */
      const resolutions = [];
      for (let index = 0; index < eventModel.ResolutionsInfo.length; index++) {
        if (itemId !== eventModel.ResolutionsInfo[index].Resolution.Id) {
          resolutions.push({
            ...eventModel.ResolutionsInfo[index],
            ResolutionId: eventModel.ResolutionsInfo[index].Id,
            EventId: eventModel?.Id,
          });
        }
      }

      if (eventModel?.Id > 0) {
        const deleteResult = await deleteResolutionFromEvent(itemId);
        if (!deleteResult.ok) {
          return;
        }
      }

      setEventModel({
        ...eventModel,
        ResolutionsInfo: resolutions,
        Resolutions: convertResolutionsToRequest(resolutions),
      });
    }
  };

  const isResolutionSelected = () => {
    if (selectedResolution) {
      return true;
    }

    return false;
  };

  return (
    <div style={{ width: "100%" }}>
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      <Box className="flex flex-col gap-2">
        <section className="flex items-center w-full gap-2">
          <div className="w-full">
            <Combobox
              fieldLabel={localize.resolution}
              fieldValue={selectedResolution}
              fieldOptions={resolutionsData ?? []}
              popperFullWidth={PopperFullWidth}
              customOptionLabel={(option) =>
                option.Code ? `${option.Code}: ${option.Name}` : option.Name
              }
              handleValueChange={handleResolutionSelection}
              id="ResolutionSelection"
            />
          </div>

          <div>
            <Button
              disabled={!isResolutionSelected()}
              size="small"
              variant="contained"
              color="success"
              className="app_general_button_data app_success_button btn-sm"
              onClick={handleAddBtn}
            >
              <span className="mr-2">{localize.add}</span>
              <Add className="text-lg" />
            </Button>
          </div>
        </section>

        <Divider />

        <List className="w-full flex flex-col gap-2">
          {eventModel?.ResolutionsInfo?.length > 0 ? (
            eventModel.ResolutionsInfo.map((item) => (
              <ResolutionItem
                resolution={item}
                onDelete={() => {
                  handleDeleteRequest(item.Resolution.Id);
                }}
              />
            ))
          ) : (
            <NotificationItem notification={{ message: localize.no_data }} />
          )}
        </List>
      </Box>
    </div>
  );
}
